import { ConfigProvider, App as MyApp, Empty, theme } from "antd";
import Guard, { GuardProvider } from "./Guard";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import LoginPage from "./pages/LoginPage";
import MasterPage from "./pages/MasterPage";
import CompanyPoliciesPage from "./pages/CompanyPoliciesPage";
import IncidentsPage from "./pages/IncidentsPage";
import UserProfilePage from "./pages/UserProfilePage";
import NotFoundPage from "./pages/NotFoundPage";
import ErrorBoundary from "./components/ErrorBoundary";
import { StyleProvider } from "@ant-design/cssinjs";
import { useTheme } from "./Theme";
import HomePage from "./pages/HomePage";
import AwarenessPage from "./pages/AwarenessPage";
import CalendarPage from "./pages/CalendarPage";
import CompanyUsersPage from "./pages/CompanyUsersPage";
import CompanyEmployeesPage from "./pages/CompanyEmployeesPage";
import OrganizationalMetricsPage from "./pages/OrganizationalMetricsPage";
import WellBeingPage from "./pages/WellBeingPage";
import "./index.css";
import KeyProcessesOverviewPage from "./pages/KeyProcessesOverviewPage";
import ProcessStagePage from "./pages/ProcessStagePage";
import CompanyAccountPage from "./pages/CompanyAccountPage";
import ActionsOverviewPage from "./pages/ActionsOverviewPage";
import PolicyCommitmentPage from "./pages/PolicyCommitmentPage";
import PlanningActionsPage from "./pages/PlanningActionsPage";
import EvaluationReportingPage from "./pages/EvaluationReportingPage";
import ImplementationPage from "./pages/ImplementationPage";
import ManagementReviewPage from "./pages/ManagementReviewPage";
import CompanyAnalyticsPage from "./pages/CompanyAnalyticsPage";
import NotificationsPage from "./pages/NotificationsPage";
import ContactPage from "./pages/ContactPage";
import ActionPlaceholderPage from "./pages/ActionPlaceholderPage";
import CreateEventPage from "./pages/CreateEventPage";
import LeadershipBoardPage from "./pages/LeadershipBoardPage";
import AssignOceanIvPage from "./pages/AssignOceanIvPage";
import SendSurveyPage from "./pages/SendSurveyPage";
import CreateRewardsPage from "./pages/CreateRewardsPage";

function App() {
  const { isDark } = useTheme();

  return (
    <ConfigProvider
      renderEmpty={() => (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span style={{ color: "#64748b", fontSize: 13 }}>
              Nothing here yet
            </span>
          }
        />
      )}
      theme={{
        algorithm: isDark ? [theme.darkAlgorithm] : [theme.defaultAlgorithm],
        cssVar: true,
        token: {
          colorPrimary: "#14a098",
          colorInfo: "#14a098",
          colorSuccess: "#52c41a",
          colorWarning: "#f6a66b",
          colorError: "#f06364",
          colorLink: "#14a098",
          borderRadius: 10,
          fontFamily:
            "'Inter', 'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        },
        components: {
          Layout: {
            bodyBg: "#f7fafb",
            headerBg: "#f7fafb",
            siderBg: "transparent",
            triggerBg: "#eef5f5",
            triggerColor: "#0d7a73",
          },
          Menu: {
            itemSelectedBg: "#d6f3f1",
            itemSelectedColor: "#0d7a73",
            itemHoverBg: "#e8f6f5",
          },
          Button: {
            primaryShadow: "0 4px 10px rgba(20, 160, 152, 0.25)",
          },
          Card: {
            borderRadiusLG: 14,
          },
        },
      }}
    >
      <StyleProvider hashPriority="low">
        <MyApp className="flex flex-auto flex-col overflow-hidden">
         <ErrorBoundary>
          <GuardProvider>
            <BrowserRouter>
              <Routes>
                <Route
                  path="login"
                  element={
                    <Guard public>
                      <LoginPage />
                    </Guard>
                  }
                />
                <Route
                  path="/*"
                  element={
                    <Guard redirectTo="./login">
                      <MasterPage />
                    </Guard>
                  }
                >
                  {/* Root — land on home */}
                  <Route index element={<Navigate to="home" replace />} />
                  {/* HOME */}
                  <Route path="home" element={<HomePage />} />

                  {/* COMPANY PROFILE */}
                  <Route path="profile" element={<CompanyAccountPage />} />
                  <Route path="profile/users" element={<CompanyUsersPage />} />
                  <Route
                    path="profile/employees"
                    element={<CompanyEmployeesPage />}
                  />
                  <Route
                    path="profile/analytics"
                    element={<CompanyAnalyticsPage />}
                  />
                  <Route
                    path="profile/metrics"
                    element={<OrganizationalMetricsPage />}
                  />
                  <Route path="profile/calendar" element={<CalendarPage />} />

                  {/* KEY PROCESSES */}
                  <Route
                    path="processes/overview"
                    element={<KeyProcessesOverviewPage />}
                  />
                  <Route
                    path="processes/stage/:stageUid"
                    element={<ProcessStagePage />}
                  />
                  {/* Legacy static stage routes — superseded by the data-driven stage page above; kept (unlinked) for now. */}
                  <Route
                    path="processes/awareness-stage"
                    element={<AwarenessPage />}
                  />
                  <Route
                    path="processes/policy-commitment"
                    element={<PolicyCommitmentPage />}
                  />
                  <Route
                    path="processes/planning-assessment"
                    element={<PlanningActionsPage />}
                  />
                  <Route
                    path="processes/evaluation-reporting"
                    element={<EvaluationReportingPage />}
                  />
                  <Route
                    path="processes/implementation"
                    element={<ImplementationPage />}
                  />
                  <Route
                    path="processes/management-review"
                    element={<ManagementReviewPage />}
                  />

                  {/* COMPANY POLICIES */}
                  <Route path="policies" element={<CompanyPoliciesPage />} />
                  <Route path="incidents" element={<IncidentsPage />} />

                  {/* ACTIONS */}
                  <Route
                    path="actions/overview"
                    element={<ActionsOverviewPage />}
                  />
                  <Route
                    path="actions/assign-oceaniv"
                    element={<AssignOceanIvPage />}
                  />
                  <Route
                    path="actions/create-programs"
                    element={
                      <ActionPlaceholderPage
                        title="Create Programs"
                        description="Assigning wellbeing programs to employees relies on /WellbeingMaterialsAssignment which is currently role-gated to AppAdmin on the backend. This surface will go live once the Company-side assignment permission is granted."
                      />
                    }
                  />
                  <Route
                    path="actions/send-updates"
                    element={
                      <ActionPlaceholderPage
                        title="Send Company Update"
                        description="Sending notifications is currently restricted to Admin users on the backend (NotificationsController is role-gated to AppAdmin). The Company-side send endpoint is pending backend permission expansion before this surface can go live."
                      />
                    }
                  />
                  <Route
                    path="actions/send-survey"
                    element={<SendSurveyPage />}
                  />
                  <Route
                    path="actions/create-events"
                    element={<CreateEventPage />}
                  />
                  <Route
                    path="actions/create-rewards"
                    element={<CreateRewardsPage />}
                  />
                  <Route
                    path="actions/healthcare-plan"
                    element={
                      <ActionPlaceholderPage
                        title="Healthcare Plan"
                        description="A Healthcare Plan surface isn't detailed in PD-02 and no backend endpoint exists for it yet. The scope, fields and workflow are pending client specification."
                      />
                    }
                  />
                  <Route
                    path="actions/leadership-board"
                    element={<LeadershipBoardPage />}
                  />
                  {/* WELL-BEING CORNER */}
                  <Route path="well-being" element={<WellBeingPage />} />

                  {/* YOUR PROFILE */}
                  <Route path="user-profile" element={<UserProfilePage />} />

                  {/* NOTIFICATIONS / CONTACT */}
                  <Route path="notifications" element={<NotificationsPage />} />
                  <Route path="contact" element={<ContactPage />} />

                  <Route path="*" element={<NotFoundPage />} />
                </Route>
              </Routes>
            </BrowserRouter>
          </GuardProvider>
         </ErrorBoundary>
        </MyApp>
      </StyleProvider>
    </ConfigProvider>
  );
}

export default App;
